import { ColumnDef } from "@tanstack/react-table";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import {
  EXPORT_TYPES,
  REPORT_NAMES,
  TABLE_IDS,
  tableColumnsAr,
  tableColumnsEn,
} from "../../../constants/common";
import {
  FRError,
  ExportFRErrorsReportForExcelPDFBody,
} from "../../../hooks/report/interface";
import ViewAttachmentModal from "../../modal/ViewAttachmentModal";
import {
  useExportFRErrorsReportForExcel,
  useExportFRErrorsReportForPDF,
} from "../../../hooks/report/mutation";
import { downloadFile } from "../../../lib/functions";
import StatusCodes from "../../../constants/StatusCodes";

interface FrErrorsReportTableProps {
  allErrors: FRError[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  exportBody?: ExportFRErrorsReportForExcelPDFBody;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const FrErrorsReportTable = ({
  allErrors,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  exportBody,
  pageSize,
  setPageSize,
}: FrErrorsReportTableProps) => {
  const { i18n, t } = useTranslation();

  const [imageSrc, setImageSrc] = useState("");
  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);

  const { mutateAsync: ExportFRErrorsReportForExcel } =
    useExportFRErrorsReportForExcel();
  const { mutateAsync: ExportFRErrorsReportForPDF } =
    useExportFRErrorsReportForPDF();

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);

  const logsColumns: ColumnDef<FRError>[] = useMemo(
    () => [
      {
        accessorKey: "s_AppointmentCode",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("referenceNumber")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_AppointmentCode}</span>;
        },
      },
      {
        accessorKey: "s_VisitorName",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("fullName")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_VisitorName}</span>;
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_BuildingName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("building")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_BuildingName_En
                : row.original.s_BuildingName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_DeviceName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("device")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_DeviceName_En
                : row.original.s_DeviceName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_ErrorDetails",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("errorDetails")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_ErrorDetails}</span>;
        },
      },
    ],
    [i18n.language, t]
  );

  const onExport = useCallback(
    async (type: EXPORT_TYPES) => {
      if (!exportBody) return;

      if (visibleColumnKeys.length === 0) {
        toast.error(t("noColumnSelectedForExport"));
        return;
      }

      try {
        const body = {
          ...exportBody,
          ExportOptions: visibleColumnKeys.map((key) => ({
            ColumnKey: key,
            ColumnNameEn: tableColumnsEn[key],
            ColumnNameAr: tableColumnsAr[key],
            CurrentLanguage: i18n.language,
          })),
        };

        let res;
        if (type === EXPORT_TYPES.EXCEL) {
          res = await ExportFRErrorsReportForExcel(body);
        } else {
          res = await ExportFRErrorsReportForPDF(body);
        }
        downloadFile(res, REPORT_NAMES.FR_ERRORS_REPORT);
        toast.success(t("exportSuccess"));
      } catch (error: any) {
        if (error.statusCode === StatusCodes.NOT_FOUND) {
          toast.error(t("noDataToExport"));
        } else {
          toast.error(t("somethingWentWrong"));
        }
      }
    },
    [
      ExportFRErrorsReportForExcel,
      ExportFRErrorsReportForPDF,
      exportBody,
      i18n.language,
      t,
      visibleColumnKeys,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allErrors}
        columns={logsColumns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isExportExcel
        onExport={onExport}
        isSearch
        tableId={TABLE_IDS.FR_ERRORS_REPORT}
        setVisibleColumnKeys={setVisibleColumnKeys}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <ViewAttachmentModal
        attachment={imageSrc}
        contentType="image/png"
        isOpen={isViewAttachmentModalOpen}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />
    </>
  );
};

export default memo(FrErrorsReportTable);
